import { useState, useEffect, useCallback } from 'react';
import SnapshotDataService from '../services/SnapshotDataService';
import { useDebouncedCallback } from './useDebounce';

/**
 * Custom hook for managing snapshot months with caching and debouncing
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {number} options.debounceDelay - Debounce delay in ms (default: 300)
 * @param {boolean} options.forceRefresh - Force refresh on mount (default: false)
 * @returns {Object} { months, loading, error, refetch, clearCache }
 */
export const useSnapshotMonths = (options = {}) => {
  const {
    autoFetch = true,
    debounceDelay = 300,
    forceRefresh = false
  } = options;

  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Fetch function
  const fetchSnapshotMonths = useCallback(async (force = false) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching snapshot months...', { force, lastFetch });
      
      const result = await SnapshotDataService.getSnapshotMonths(force);
      
      // Handle different response formats
      const monthList = Array.isArray(result) ? result : (result?.months || []);
      
      setMonths(monthList);
      setLastFetch(new Date().toISOString());
      
      console.log('✅ Snapshot months loaded:', monthList.length, 'months');
    } catch (err) {
      console.error('❌ Error fetching snapshot months:', err);
      setError(err.message || 'Failed to fetch snapshot months');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced fetch function
  const debouncedFetch = useDebouncedCallback(fetchSnapshotMonths, debounceDelay, []);

  // Refetch function
  const refetch = useCallback((force = true) => {
    debouncedFetch(force);
  }, [debouncedFetch]);

  // Clear cache function
  const clearCache = useCallback(() => {
    import('../services/ApiCacheService').then(apiCacheService => {
      apiCacheService.default.clearCacheEntry('snapshot-months');
      console.log('🧹 Snapshot months cache cleared');
    });
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      debouncedFetch(forceRefresh);
    }
  }, [autoFetch, forceRefresh, debouncedFetch]);

  return {
    months,
    loading,
    error,
    refetch,
    clearCache,
    lastFetch,
    isEmpty: months.length === 0,
    hasError: !!error
  };
};

export default useSnapshotMonths;


